import {
  ColumnsPicker,
  i18nKeyForError,
  isSessionError,
  NoIdentityState,
  PerPageSelect,
  PINNED_BLOCK,
  parseApiError,
  usePaginationLabels,
  WIDE_PAGE,
} from '@features/allocation';
import { useMe } from '@features/auth';
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Pagination,
  Skeleton,
} from '@ntconsult/aluma-react';
import { Download, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportProjectList } from '../hooks/useExportProjectList';
import { PROJECT_COLUMN_LABEL_KEYS, useProjectColumns } from '../hooks/useProjectColumns';
import { useProjectFilters } from '../hooks/useProjectFilters';
import { useProjectList } from '../hooks/useProjectList';
import type { ProjectRow } from '../types/ProjectList';
import { EditProjectModal } from './EditProjectModal';
import { ProjectExportDialog } from './ProjectExportDialog';
import { ProjectFiltersDrawer } from './ProjectFiltersDrawer';
import { ProjectListFilterChips } from './ProjectListFilterChips';
import { ProjectListTable } from './ProjectListTable';

/**
 * Consulta de projetos do Axiis (`ProjetoHeaderList.php` do legado), só leitura: os projetos vêm da
 * importação diária do Access BR/US. Admin e gestor; o back limita o gestor ao escopo dele.
 */
export function ProjectListPage() {
  const { t } = useTranslation(['projetos', 'alocacao']);
  const paginationLabels = usePaginationLabels();
  const filterState = useProjectFilters();
  const query = useProjectList(filterState.filters);
  const columnsState = useProjectColumns();
  const isAdmin = useMe().data?.roles.includes('admin') ?? false;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [editing, setEditing] = useState<ProjectRow | null>(null);
  const exportProjects = useExportProjectList(
    filterState.filters,
    filterState.perPage,
    columnsState.visibleColumns,
  );

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;
  // Sem filtro e sem nenhum projeto, o estado vazio substitui a tabela.
  const noProjects = data && data.total === 0 && !filterState.hasActiveFilters;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_PAGE}`}>
      <div className={PINNED_BLOCK}>
        <PageHeader title={t('projetos:titulo')} description={t('projetos:descricao')} />
      </div>

      <div className={PINNED_BLOCK}>
        <Alert variant="info" title={t('projetos:avisoEdicao')} />
      </div>

      <section className="flex min-w-0 flex-col gap-4" aria-label={t('projetos:titulo')}>
        <div className={`flex flex-wrap items-center gap-2 ${PINNED_BLOCK}`}>
          {!isListLoading && data && (
            <span className="text-sm text-foreground-muted" aria-live="polite">
              {t('projetos:total', { count: data.total, showing: data.items.length })}
            </span>
          )}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<SlidersHorizontal size={14} />}
            onClick={() => setAdvancedOpen(true)}
          >
            {t('alocacao:filtros.avancados')}
          </Button>
          <ColumnsPicker
            columnsState={columnsState}
            labelKeys={PROJECT_COLUMN_LABEL_KEYS}
            namespace="projetos"
          />
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download size={14} />}
            disabled={!data?.total}
            onClick={() => setExportOpen(true)}
          >
            {t('projetos:exportacao.botao')}
          </Button>
        </div>

        <div className={PINNED_BLOCK}>
          <ProjectListFilterChips filterState={filterState} />
        </div>

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'alocacao:erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && noProjects && (
          <EmptyState
            title={t('projetos:tabela.vazioTitulo')}
            description={t('projetos:tabela.vazioDescricao')}
          />
        )}

        {!isListLoading && !isError && data && !noProjects && (
          <div className="flex flex-col gap-4">
            <ProjectListTable
              items={data.items}
              filterState={filterState}
              columnsState={columnsState}
              isAdmin={isAdmin}
              onEdit={setEditing}
            />
            <div className={`flex flex-wrap items-center gap-4 ${PINNED_BLOCK}`}>
              <PerPageSelect value={filterState.perPage} onChange={filterState.setPerPage} />
              {totalPages > 1 && (
                <Pagination
                  {...paginationLabels}
                  page={filterState.filters.page}
                  totalPages={totalPages}
                  onPageChange={filterState.goToPage}
                />
              )}
            </div>
          </div>
        )}
      </section>

      <ProjectFiltersDrawer
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        filterState={filterState}
      />
      <ProjectExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        exportMutation={exportProjects}
        pageCount={data?.items.length ?? 0}
        totalCount={data?.total ?? 0}
        hasActiveFilters={filterState.hasActiveFilters}
      />
      {isAdmin && (
        <EditProjectModal
          open={editing !== null}
          onClose={() => setEditing(null)}
          project={editing}
        />
      )}
    </div>
  );
}
